import React, { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Paper, Stack, TextField, Typography, List, ListItem, ListItemText, ListItemAvatar, Avatar, ListItemSecondaryAction, IconButton } from '@mui/material';
import { ArrowBack as BackIcon, Comment as CommentIcon, AttachFile as AttachIcon, Edit as EditIcon, Download as DownloadIcon, InsertDriveFile as FileIcon, PictureAsPdf as PdfIcon, VideoFile as VideoIcon, TableView as ExcelIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';
import * as XLSX from 'xlsx';

export default function TaskDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();

  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [attachmentsCount, setAttachmentsCount] = useState(0);
  const [attachmentsList, setAttachmentsList] = useState([]);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [excelData, setExcelData] = useState(null);

  const handlePreview = async (file) => {
    const realName = (file.file_path || '').split(/[/\\]/).pop();
    // Ensure API_BASE_URL is correct or fallback to localhost:5002
    const baseUrl = API_BASE_URL || 'http://localhost:5002';
    const url = `${baseUrl}/uploads/${realName}`;
    const ext = (file.file_name || '').split('.').pop().toLowerCase();
    
    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      try {
        const res = await fetch(url);
        const ab = await res.arrayBuffer();
        const wb = XLSX.read(ab, { type: 'array' });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const html = XLSX.utils.sheet_to_html(ws);
        setExcelData(html);
        setPreviewFile({ ...file, type: 'excel', url });
      } catch (e) {
        alert('Failed to preview Excel file');
      }
    } else if (['mp4', 'webm', 'ogg'].includes(ext)) {
      setPreviewFile({ ...file, type: 'video', url });
    } else if (['pdf'].includes(ext)) {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        setPreviewFile({ ...file, type: 'pdf', url: objectUrl });
      } catch (e) {
        setPreviewFile({ ...file, type: 'pdf', url });
      }
    } else if (['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
      setPreviewFile({ ...file, type: 'image', url });
    } else {
      window.open(url, '_blank');
    }
  };

  const closePreview = () => {
    if (previewFile?.url && previewFile.url.startsWith('blob:')) {
      URL.revokeObjectURL(previewFile.url);
    }
    setPreviewFile(null);
    setExcelData(null);
  };

  const [form, setForm] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    status: 'Task Ready',
    assigned_to: ''
  });

  const capFirst = (s) => {
    const t = String(s || '').trim();
    return t ? t.charAt(0).toUpperCase() + t.slice(1) : '';
  };

  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';

  const canViewComments = () => {
    if (!task || !user) return false;
    return isAdmin || Number(task.assigned_to || 0) === Number(user.id) || Number(task.created_by || 0) === Number(user.id);
  };

  const loadTask = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to load task');
        navigate('/tasks');
        return;
      }
      setTask(data.task);
      setForm({
        title: data.task.title || '',
        description: data.task.description || '',
        priority: data.task.priority || 'Medium',
        status: data.task.status || 'Task Ready',
        assigned_to: data.task.assigned_to || ''
      });
    } catch (e) {}
  };

  const loadComments = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks/${id}/comments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) {
        setComments(data.comments || []);
      }
    } catch (e) {}
  };

  const loadUsers = async () => {
    if (!isAdmin) return;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setUsers(data.users || []);
    } catch (e) {}
  };

  useEffect(() => {
    loadTask();
    loadComments();
    loadUsers();
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_BASE_URL}/api/tasks/${id}/attachments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok) {
          const arr = Array.isArray(data.attachments) ? data.attachments : [];
          setAttachmentsCount(arr.length);
          setAttachmentsList(arr);
        }
      } catch (e) {}
    })();
  }, [id]);

  const handleAddComment = async () => {
    const body = String(newComment || '').trim();
    if (!body) return;
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE_URL}/api/tasks/${id}/comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ body }),
      });
      const data = await res.json();
      if (res.ok) {
        setNewComment('');
        setComments(data.comments || []);
      } else {
        alert(data.error || 'Failed to add comment');
      }
    } catch (e) {
    } finally {
      setLoading(false);
    }
  };

  if (!task) {
    return (
      <Layout title="Task Details">
        <Box sx={{ p: 3 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Task Details">
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/tasks')}
            sx={{
              bgcolor: '#e2e8f0',
              color: '#111827',
              '&:hover': { bgcolor: '#cbd5e1' },
              borderRadius: 2,
              px: 1.75,
            }}
          >
            Back to Tasks
          </Button>
        </Stack>

        <Paper
          sx={{
            p: 2,
            borderRadius: 2,
            mb: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 0.5 }}>
              <Chip
                label={task.priority || 'Medium'}
                color={task.priority === 'High' ? 'error' : task.priority === 'Low' ? 'success' : 'warning'}
                sx={{ fontWeight: 'bold' }}
              />
              <Typography variant="h5" fontWeight="bold">{task.title}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Task ID: #{task.id}
            </Typography>
          </Box>
          <Chip label={task.status} sx={{ bgcolor: '#e5e7eb', color: '#111827', borderRadius: 2 }} />
        </Paper>

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'stretch', flexWrap: 'nowrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 2, borderRadius: 2, height: '100%', width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Task Information</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Description:</strong> {task.description || '—'}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Typography variant="body2">
                  <strong>Assigned To:</strong>{' '}
                  <span style={{ color: '#2563eb' }}>{task.assignee_name || task.assignee_username || 'Unassigned'}</span>
                </Typography>
                <Typography variant="body2">
                  <strong>Created By:</strong>{' '}
                  {task.creator_name || task.creator_username || task.creator_email || '—'}
                </Typography>
              </Stack>
            </Paper>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Paper sx={{ p: 2, borderRadius: 2, height: '100%', width: '100%' }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Timeline & Statistics</Typography>
              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Created:</strong> {new Date(task.created_at).toLocaleString()}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Paper
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                  variant="outlined"
                >
                  <CommentIcon fontSize="small" />
                  <Box>
                    <Typography variant="subtitle2">{comments.length}</Typography>
                    <Typography variant="caption" color="text.secondary">Comments</Typography>
                  </Box>
                </Paper>
                <Paper
                  onClick={() => setAttachmentsOpen(true)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: '#f5f5f5' }
                  }}
                  variant="outlined"
                >
                  <AttachIcon fontSize="small" />
                  <Box>
                    <Typography variant="subtitle2">{attachmentsCount}</Typography>
                    <Typography variant="caption" color="text.secondary">Attachments</Typography>
                  </Box>
                </Paper>
              </Stack>
            </Paper>
          </Box>
        </Box>

        {isAdmin && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Admin Actions</Typography>
              <Button
                startIcon={<EditIcon />}
                onClick={() => setEditOpen(true)}
                sx={{
                  bgcolor: '#e0f2fe',
                  color: '#0369a1',
                  borderRadius: 2,
                  '&:hover': { bgcolor: '#bae6fd' },
                }}
              >
                Edit Task
              </Button>
            </Paper>
          </Box>
        )}

        <Box sx={{ mt: 3 }}>
          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <CommentIcon fontSize="small" />
              <Typography variant="h6">Comments</Typography>
            </Stack>
            {canViewComments() ? (
              <>
                <Box sx={{ 
                  maxHeight: '500px', 
                  overflowY: 'auto', 
                  mb: 2, 
                  p: 2, 
                  bgcolor: '#f0f4f8', 
                  borderRadius: 2,
                  border: '1px solid #e2e8f0'
                }}>
                  <Stack spacing={1.5}>
                    {comments.map((c) => {
                      const isAssigned = Number(c.user_id) === Number(task.assigned_to);
                      const align = isAssigned ? 'flex-end' : 'flex-start';
                      const bgcolor = isAssigned ? '#dcf8c6' : '#fff'; 
                      
                      return (
                        <Box key={c.id} sx={{ display: 'flex', justifyContent: align }}>
                          <Paper
                            elevation={0}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              maxWidth: '75%',
                              bgcolor: bgcolor,
                              border: '1px solid',
                              borderColor: isAssigned ? '#dcf8c6' : '#e2e8f0',
                              position: 'relative'
                            }}
                          >
                            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 0.5 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#333', fontSize: '0.8rem' }}>
                                {capFirst(c.user_name || 'User')}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {new Date(c.created_at).toLocaleString()}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                              {c.body}
                            </Typography>
                          </Paper>
                        </Box>
                      );
                    })}
                    {comments.length === 0 && (
                      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                        No comments yet. Start the conversation!
                      </Typography>
                    )}
                  </Stack>
                </Box>
                <TextField
                  placeholder="Type a message..."
                  multiline
                  fullWidth
                  minRows={2}
                  maxRows={6}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    disabled={loading}
                    onClick={handleAddComment}
                    sx={{ bgcolor: '#1a73e8', '&:hover': { bgcolor: '#1658b5' }, borderRadius: 2 }}
                  >
                    Add Comment
                  </Button>
                </Box>
              </>
            ) : (
              <Typography variant="body2">Comments are visible to assigned user and superadmin only.</Typography>
            )}
          </Paper>
        </Box>

        <Dialog open={attachmentsOpen} onClose={() => setAttachmentsOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Attachments</DialogTitle>
          <DialogContent>
            {attachmentsList.length === 0 ? (
              <Typography>No attachments</Typography>
            ) : (
              <List>
                {attachmentsList.map((f) => {
                  const realName = (f.file_path || '').split(/[/\\]/).pop();
                  const url = `${API_BASE_URL}/uploads/${realName}`;
                  const ext = (f.file_name || '').split('.').pop().toLowerCase();
                  const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                  const isVideo = ['mp4', 'webm', 'ogg'].includes(ext);
                  const isPdf = ['pdf'].includes(ext);
                  const isExcel = ['xlsx', 'xls', 'csv'].includes(ext);

                  let IconComp = FileIcon;
                  if (isVideo) IconComp = VideoIcon;
                  if (isPdf) IconComp = PdfIcon;
                  if (isExcel) IconComp = ExcelIcon;

                  return (
                    <ListItem key={f.id}>
                      <ListItemAvatar>
                        {isImage ? (
                          <Avatar
                            variant="rounded"
                            src={url}
                            alt={f.file_name}
                            sx={{ width: 50, height: 50, mr: 2, cursor: 'pointer', border: '1px solid #eee' }}
                            onClick={() => handlePreview(f)}
                          />
                        ) : (
                          <Avatar sx={{ cursor: 'pointer' }} onClick={() => handlePreview(f)}>
                            <IconComp />
                          </Avatar>
                        )}
                      </ListItemAvatar>
                      <ListItemText 
                        primary={f.file_name} 
                        secondary={new Date(f.created_at).toLocaleString()} 
                        sx={{ cursor: 'pointer' }}
                        onClick={() => handlePreview(f)}
                      />
                      <ListItemSecondaryAction>
                        <IconButton edge="end" onClick={() => window.open(url, '_blank')}>
                          <DownloadIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAttachmentsOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Task</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Task Title"
                fullWidth
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={3}
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <TextField
                select
                label="Priority"
                fullWidth
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: e.target.value })}
              >
                <MenuItem value="High">High</MenuItem>
                <MenuItem value="Medium">Medium</MenuItem>
                <MenuItem value="Low">Low</MenuItem>
              </TextField>
              <TextField
                select
                label="Status"
                fullWidth
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <MenuItem value="Task Ready">Task Ready</MenuItem>
                <MenuItem value="In Progress">In Progress</MenuItem>
                <MenuItem value="Needs Review">Needs Review</MenuItem>
                <MenuItem value="Done">Done</MenuItem>
              </TextField>
              {isAdmin && (
                <TextField
                  select
                  label="Assign To"
                  fullWidth
                  value={form.assigned_to}
                  onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}
                >
                  <MenuItem value=""><em>Unassigned</em></MenuItem>
                  {users.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.full_name} ({u.role})
                    </MenuItem>
                  ))}
                </TextField>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={async () => {
                try {
                  const token = localStorage.getItem('token');
                  const res = await fetch(`${API_BASE_URL}/api/tasks/${task.id}`, {
                    method: 'PUT',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify(form),
                  });
                  const data = await res.json();
                  if (res.ok) {
                    setTask(data.task);
                    setEditOpen(false);
                  } else {
                    alert(data.error || 'Failed to update task');
                  }
                } catch (e) {}
              }}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!previewFile} onClose={closePreview} maxWidth="lg" fullWidth>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {previewFile?.file_name}
            <Box>
              <IconButton onClick={() => window.open(previewFile?.url, '_blank')} title="Download">
                <DownloadIcon />
              </IconButton>
              <Button onClick={closePreview}>Close</Button>
            </Box>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0, bgcolor: '#f5f5f5', minHeight: '60vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            {previewFile?.type === 'image' && (
              <img src={previewFile.url} alt="Preview" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
            )}
            {previewFile?.type === 'video' && (
              <video controls src={previewFile.url} style={{ maxWidth: '100%', maxHeight: '80vh' }} />
            )}
            {previewFile?.type === 'pdf' && (
              <embed src={previewFile.url} type="application/pdf" width="100%" height="600px" />
            )}
            {previewFile?.type === 'excel' && excelData && (
              <Box sx={{ width: '100%', height: '600px', overflow: 'auto', p: 2, bgcolor: 'white' }}>
                <style>{`
                  table { border-collapse: collapse; width: 100%; }
                  th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                  th { background-color: #f2f2f2; }
                `}</style>
                 <div dangerouslySetInnerHTML={{ __html: excelData }} />
              </Box>
            )}
            {!['image', 'video', 'pdf', 'excel'].includes(previewFile?.type) && (
               <Typography>Preview not available. Please download the file.</Typography>
            )}
          </DialogContent>
        </Dialog>
      </Box>
    </Layout>
  );
}
